// src/components/GoogleAdsConnection.jsx
//
// The Google Ads grant, as something an operator can see and change.
//
// Every /api/google endpoint acts as one Google identity, using the refresh
// token the backend stored for GOOGLE_ADS_DEFAULT_EMAIL. Until now that token
// arrived by running leadsmanagement/get_refresh_token.py over SSH and pasting
// an authorization code, so when the grant lapsed the Ads pages failed with
// "No refresh token stored for default email …" and nobody without a shell on
// the VPS could do anything about it.

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiActivity,
  FiCheckCircle,
  FiAlertTriangle,
  FiLink,
  FiLoader,
  FiRefreshCw,
  FiSlash,
} from 'react-icons/fi';

import { adsApi, connectUrl } from '../lib/googleAds';

function formatWhen(iso) {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleString();
}

/**
 * `onChange(connected)` fires whenever the connection state is known or
 * changes, so the page can decide whether it is worth fetching campaigns.
 */
export default function GoogleAdsConnection({ onChange, next = '/google-ads' }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Set by the callback redirect: /google-ads?google_connected=… or ?google_error=…
  const [outcome, setOutcome] = useState(null);

  const load = useCallback(async () => {
    try {
      const out = await adsApi('/auth/google/status');
      setStatus(out);
      setError('');
      onChange?.(Boolean(out.connected));
    } catch (e) {
      setError(e.message);
      onChange?.(false);
    }
  }, [onChange]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('google_connected');
    const failed = params.get('google_error');
    if (connected || failed) {
      setOutcome(connected
        ? { ok: true, text: `Connected as ${connected}.` }
        : { ok: false, text: failed });
      // Drop the parameters so a refresh does not replay the banner, and so the
      // email does not sit in the address bar.
      params.delete('google_connected');
      params.delete('google_error');
      const query = params.toString();
      window.history.replaceState(
        {}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }
    load();
  }, [load]);

  // A single call that proves the whole chain: stored grant, developer token,
  // manager id, and permission on the customer account. Worth its own button
  // because "connected" only means a refresh token is on file — it says
  // nothing about whether Google will honour it for this account.
  const testConnection = async () => {
    setBusy(true);
    setOutcome(null);
    try {
      const out = await adsApi('/google/mcc/sanity');
      setOutcome(out.ok
        ? { ok: true, text: `Reached ${out.name || out.customer_id} (${out.currency_code}, ${out.time_zone}).` }
        : { ok: false, text: out.message || 'The account returned no rows.' });
    } catch (e) {
      setOutcome({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      'Disconnect Google Ads? Campaigns, budgets and stats will stop loading '
      + 'until someone connects the account again.')) return;
    setBusy(true);
    try {
      const out = await adsApi('/auth/google/disconnect', { method: 'POST' });
      setOutcome({ ok: true, text: out.message });
      await load();
    } catch (e) {
      setOutcome({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const configured = status?.configured || {};
  // The consent flow cannot even start without these three, so the button
  // would only lead to an error page.
  const canConnect = Boolean(
    configured.client_id && configured.client_secret && configured.app_secret_key);
  const connected = Boolean(status?.connected);
  const connectedAt = formatWhen(status?.connected_at);

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          {status === null && !error ? (
            <FiLoader className="mt-0.5 h-5 w-5 animate-spin text-gray-400" />
          ) : connected ? (
            <FiCheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
          ) : (
            <FiAlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          )}

          <div className="min-w-0">
            <div className="font-semibold text-gray-900">
              {status === null && !error
                ? 'Checking the Google Ads connection…'
                : connected
                  ? 'Google Ads is connected'
                  : 'Google Ads is not connected'}
            </div>
            <div className="mt-0.5 text-sm text-gray-600 break-words">
              {error
                ? error
                : connected
                  ? <>
                      Acting as <span className="font-medium">{status.email}</span>
                      {connectedAt && <> · granted {connectedAt}</>}
                    </>
                  : status
                    ? 'Campaigns, budgets and statistics cannot load until an '
                      + 'account is connected.'
                    : null}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <a
            href={canConnect ? connectUrl(next) : undefined}
            aria-disabled={!canConnect}
            onClick={e => { if (!canConnect) e.preventDefault(); }}
            className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-black ${
              canConnect ? 'hover:opacity-90' : 'cursor-not-allowed opacity-50'
            }`}
            style={{ backgroundColor: '#F96168' }}
            title={canConnect
              ? 'Approve access on Google and come back'
              : 'Google OAuth is not configured on this server'}
          >
            {connected ? <FiRefreshCw className="h-4 w-4" /> : <FiLink className="h-4 w-4" />}
            {connected ? 'Reconnect' : 'Connect Google Ads'}
          </a>

          {connected && (
            <button
              type="button"
              onClick={testConnection}
              disabled={busy}
              className="inline-flex min-h-[42px] items-center gap-2 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Ask Google whether this grant can actually reach the account"
            >
              <FiActivity className="h-4 w-4" />
              {busy ? 'Checking…' : 'Test connection'}
            </button>
          )}

          {connected && (
            <button
              type="button"
              onClick={disconnect}
              disabled={busy}
              className="inline-flex min-h-[42px] items-center gap-2 rounded-lg border border-red-200 px-4 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <FiSlash className="h-4 w-4" />
              {busy ? 'Working…' : 'Disconnect'}
            </button>
          )}
        </div>
      </div>

      {outcome && (
        <div className={`mt-4 rounded-lg border p-3 text-sm ${
          outcome.ok
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {outcome.text}
        </div>
      )}

      {status?.hint && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {status.hint}
        </div>
      )}
    </div>
  );
}
